import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useSeo } from '@/hooks/useSeo';

function formatErr(detail) {
  if (!detail) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLoginPage() {
  useSeo({
    title: 'Accesso Amministratore',
    description: "Area riservata. Accedi per gestire il catalogo Ciro Gallotti.",
    path: '/admin',
    noindex: true,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      const redirectTo = location.state?.from || '/admin';
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-bordeaux/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-bordeaux" strokeWidth={1.5} />
          </div>
          <p className="text-eyebrow text-bordeaux mb-3">Area Riservata</p>
          <h1 className="font-display text-h2 text-ink">Accesso Amministratore</h1>
          <p className="mt-3 text-small text-muted">Inserisci le credenziali per gestire il catalogo.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-line rounded-2xl p-8 shadow-sm space-y-5">
          <div>
            <Label htmlFor="username" className="text-eyebrow text-ink">Username</Label>
            <div className="relative mt-2">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                className="pl-11"
                data-testid="admin-login-username"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-eyebrow text-ink">Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="pl-11"
                data-testid="admin-login-password"
              />
            </div>
          </div>

          {error && (
            <div
              className="flex items-start gap-3 p-3 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive text-sm"
              data-testid="admin-login-error"
              role="alert"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            size="lg"
            variant="accent"
            className="w-full"
            data-testid="admin-login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Accesso in corso…
              </>
            ) : (
              'Accedi'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-small text-muted">
          <a href="/" className="hover:text-bordeaux transition-colors duration-hover">
            ← Torna al sito
          </a>
        </p>
      </div>
    </div>
  );
}
